import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import ImageIcon from '@atlaskit/icon/glyph/image';
import MediaServicesScaleLargeIcon from '@atlaskit/icon/glyph/media-services/scale-large';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { N500, N70A, N0, B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import {
	COVER_PICTURE_REMOVE_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import {
	UnsplashDropdownMenu,
	PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD as CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD,
} from '@confluence/custom-sites-extensions';

import RepositionIcon from '../images/Reposition-NS.svg';
import type { HoverOverlayAndDropDownItemsComponentProps } from '../pageCoverPictureTypes';
import { DEFAULT_QUERY_WORD } from '../pageCoverPictureConstants';

import { ImagePickerModal } from './ImagePickerModal';
import { getUnsplashPhotos } from './unsplashAPI';

const i18n = defineMessages({
	updateImage: {
		id: 'page-cover-picture.update.header.image',
		description: 'Message user can click on to update/change their header image.',
		defaultMessage: 'Update header image',
	},
	changeImage: {
		id: 'page-cover-picture.change.image.message',
		description: 'First item in dropdown which allows user to change their header image.',
		defaultMessage: 'Change image',
	},
	repositionImage: {
		id: 'page-cover-picture.reposition.image.message',
		description: 'Second item in dropdown which allows user to reposition their header image.',
		defaultMessage: 'Reposition',
	},
	removeImage: {
		id: 'page-cover-picture.remove.image.message',
		description: 'Third item in dropdown which allows user to remove their header image.',
		defaultMessage: 'Remove image',
	},
});

type HoverOverlayProps = {
	isDropdownOpen: boolean;
	ref?: any;
	isCustomSitesPageTitleFFOn?: boolean;
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HoverOverlay = styled.div<HoverOverlayProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ isCustomSitesPageTitleFFOn, dynamicCoverImageHeight }) =>
		isCustomSitesPageTitleFFOn ? `${dynamicCoverImageHeight}px` : '140px',
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.neutral.hovered', N70A),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) =>
		props.userOffline && props.isCustomSitesPageTitleFFOn ? 'not-allowed' : 'pointer',
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.isDropdownOpen ? 1 : 0),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.userOffline && props.isCustomSitesPageTitleFFOn ? '0' : '1'),
	},
	'&:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.userOffline && props.isCustomSitesPageTitleFFOn ? '0' : '1'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownTrigger = styled.button({
	display: 'flex',
	flexFlow: 'column nowrap',
	alignItems: 'center',
	color: token('color.background.input', N0),
	cursor: 'pointer',
	background: 'none',
	border: 'none',
	outlineColor: token('color.border.information', B100),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	height: '21px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemTextWrapper = styled.div({
	margin: token('space.075', '6px'),
});

export const HoverOverlayAndDropdownItems = ({
	userOffline,
	openModal,
	closeModal,
	openMediaBrowser,
	setIsRepositionActive,
	onRemoveImage,
	onImageWidthChange,
	isModalOpenState,
	handleUnsplashUpload,
	setIsUploadingPicture,
	initialUnsplashInfoArray,
	setInitialUnsplashInfoArray,
	coverPictureState,
	isCustomSitesPageTitleFFOn,
	isExternalShare,
	contentId,
	dynamicCoverImageHeight,
}: HoverOverlayAndDropDownItemsComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const overlayComponent = useRef<HTMLElement | null>(null);
	const dropdownComponent = useRef<HTMLElement | null>(null);
	const dropdownPopupComponentRef = useRef<HTMLElement | null>(null);

	const isLivePage = useIsLivePage();

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				overlayComponent?.current &&
				event.target instanceof Node &&
				!overlayComponent?.current?.contains(event.target) &&
				!dropdownComponent?.current?.contains(event.target) &&
				(!isCustomSitesPageTitleFFOn || !dropdownPopupComponentRef?.current?.contains(event.target))
			) {
				// close parent dropdown if we click anywhere outside
				if (isCustomSitesPageTitleFFOn) {
					void coverPictureState.setIsChangingPicture(false);
				}
				setIsDropdownOpen(false);
			}
		},
		[isCustomSitesPageTitleFFOn, coverPictureState],
	);

	// took inspiration from here:
	// https://bitbucket.org/atlassian/ptc-directory-ui/src/84f964d666578de7d67d99727c734e1231b8588c/packages/common/src/components/ProfileHeader/ProfileHeader.tsx?at=master#lines-83
	// basically if we click outside of the hover overlay, close the dropdown
	useEffect(() => {
		addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	const toggleDropdownOpen = useCallback(
		(event: any) => {
			if (
				isCustomSitesPageTitleFFOn &&
				event.target instanceof Node &&
				(dropdownComponent?.current?.contains(event.target) ||
					dropdownPopupComponentRef?.current?.contains(event.target))
			) {
				// don't toggle close the dropdown if clicking in the dropdown
				// we need this since the popup is nested
				return;
			}
			if (initialUnsplashInfoArray.length === 0) {
				const isPageTitleComponent = true;
				const defaultQueryWord = isCustomSitesPageTitleFFOn
					? CUSTOM_SITES_DEFAULT_UNSPLASH_QUERY_WORD
					: DEFAULT_QUERY_WORD;
				if (isCustomSitesPageTitleFFOn) {
					void getUnsplashPhotos(defaultQueryWord, 1, isPageTitleComponent).then((results) => {
						setInitialUnsplashInfoArray(results);
					});
				} else {
					void getUnsplashPhotos(defaultQueryWord, 1).then((results) => {
						setInitialUnsplashInfoArray(results);
					});
				}
			}
			if (isCustomSitesPageTitleFFOn) {
				const isChangingPicture = coverPictureState.state.isChangingPicture;
				void coverPictureState.setIsChangingPicture(!isChangingPicture);
			}
			setIsDropdownOpen((prevIsDropdownOpen) => !prevIsDropdownOpen);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'update',
					actionSubjectId: 'updateHeaderImageButtonClicked',
					source: 'editorPageCoverPicture',
					attributes: {
						isLivePage,
					},
				},
			}).fire();
		},
		[
			initialUnsplashInfoArray,
			setInitialUnsplashInfoArray,
			createAnalyticsEvent,
			isLivePage,
			isCustomSitesPageTitleFFOn,
			coverPictureState,
		],
	);

	const onChangeImageClicked = () => {
		openModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'change',
				actionSubjectId: 'changeHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	const onRepositionClicked = () => {
		setIsRepositionActive(true);
		if (isCustomSitesPageTitleFFOn) {
			void coverPictureState.setIsChangingPicture(false);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'reposition',
				actionSubjectId: 'repositionHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
			},
		}).fire();
	};

	const onRemoveImageClicked = () => {
		experienceTracker.start({
			name: COVER_PICTURE_REMOVE_EXPERIENCE,
		});
		onRemoveImage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'remove',
				actionSubjectId: 'removeHeaderImageButtonClicked',
				source: 'editorPageCoverPicture',
				attributes: {
					isLivePage,
				},
			},
		}).fire();
	};

	const onCoverImageWidthDropdownOptionSelected = (isCoverImageWidthChanged: boolean) => {
		if (isCoverImageWidthChanged) {
			setIsDropdownOpen(false);
		}
	};

	const handleFocus = useCallback(() => {
		void coverPictureState.setIsAddHeaderImageButtonFocused(true);
	}, [coverPictureState]);

	const handleBlur = useCallback(() => {
		void coverPictureState.setIsAddHeaderImageButtonFocused(false);
	}, [coverPictureState]);

	if (isCustomSitesPageTitleFFOn) {
		return (
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions
			<div onFocus={handleFocus} onBlur={handleBlur}>
				<HoverOverlay
					data-testid="hover-overlay-custom-sites"
					isDropdownOpen={isDropdownOpen}
					onClick={userOffline ? undefined : toggleDropdownOpen}
					ref={overlayComponent as any}
					isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
					userOffline={userOffline}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
				>
					{!userOffline && (
						<UnsplashDropdownMenu
							dropdownRef={dropdownComponent}
							onDelete={onRemoveImageClicked}
							onReposition={onRepositionClicked}
							imageSrc={coverPictureState.state.coverPictureId}
							isModalOpenState={isDropdownOpen}
							openMediaBrowser={openMediaBrowser}
							initialUnsplashInfoArray={initialUnsplashInfoArray as any}
							closePopup={closeModal}
							userOffline={userOffline}
							handleUnsplashUpload={handleUnsplashUpload}
							setIsUploadingPicture={setIsUploadingPicture}
							coverPictureState={coverPictureState}
							dropdownPopupComponentRef={dropdownPopupComponentRef}
							isExternalShare={isExternalShare}
							contentId={contentId}
							onImageWidthChange={onImageWidthChange}
							onCoverImageWidthDropdownOptionSelected={onCoverImageWidthDropdownOptionSelected}
						/>
					)}
				</HoverOverlay>
			</div>
		);
	}

	// TODO(brizvash): fix incompatible csstype of emotion and compiled
	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div onFocus={handleFocus} onBlur={handleBlur}>
			<HoverOverlay
				data-testid="hover-overlay"
				isDropdownOpen={isDropdownOpen}
				onClick={toggleDropdownOpen}
				ref={overlayComponent as any}
				isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
			>
				<DropdownMenu
					placement="bottom"
					isOpen={isDropdownOpen}
					defaultOpen={false}
					trigger={({ triggerRef, ...props }) => (
						<DropdownTrigger
							data-testid="update-image-button"
							{...props}
							ref={triggerRef as React.RefObject<HTMLButtonElement>}
							tabIndex={0}
							aria-expanded={isDropdownOpen}
						>
							<ImageIcon
								size="large"
								label=""
								primaryColor={token('color.icon.inverse', 'white')}
							/>
							{formatMessage(i18n.updateImage)}
						</DropdownTrigger>
					)}
				>
					<DropdownItemGroup ref={dropdownComponent}>
						<DropdownItem onClick={onChangeImageClicked} testId="change-image-button">
							<DropdownItemWrapper>
								<MediaServicesScaleLargeIcon
									label=""
									primaryColor={token('color.icon.subtle', N500)}
								/>
								<DropdownItemTextWrapper>{formatMessage(i18n.changeImage)}</DropdownItemTextWrapper>
							</DropdownItemWrapper>
						</DropdownItem>
						<DropdownItem onClick={onRepositionClicked} testId="reposition-image-button">
							<DropdownItemWrapper>
								<img src={RepositionIcon} alt="reposition-icon" />
								<DropdownItemTextWrapper>
									{formatMessage(i18n.repositionImage)}
								</DropdownItemTextWrapper>
							</DropdownItemWrapper>
						</DropdownItem>
						<DropdownItem onClick={onRemoveImageClicked} testId="remove-image-button">
							<DropdownItemWrapper>
								<EditorRemoveIcon label="" primaryColor={token('color.icon.subtle', N500)} />
								<DropdownItemTextWrapper>{formatMessage(i18n.removeImage)}</DropdownItemTextWrapper>
							</DropdownItemWrapper>
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</HoverOverlay>
			<ModalTransition>
				{isModalOpenState && (
					<ImagePickerModal
						data-testid="image-picker-modal"
						initialUnsplashInfoArray={initialUnsplashInfoArray}
						closeModal={closeModal}
						openMediaBrowser={openMediaBrowser}
						userOffline={userOffline}
						handleUnsplashUpload={handleUnsplashUpload}
						setIsUploadingPicture={setIsUploadingPicture}
						coverPictureState={coverPictureState}
					/>
				)}
			</ModalTransition>
		</div>
	);
};
